import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { LaurelIcon } from './SpaIcons';
export function About() {
  const villageRef = useRef(null);
  const villageInView = useInView(villageRef, {
    once: true,
    margin: '-100px'
  });
  const elenaRef = useRef(null);
  const elenaInView = useInView(elenaRef, {
    once: true,
    margin: '-100px'
  });
  return <section id="about" className="w-full bg-cream py-24 md:py-32 px-6 lg:px-8" data-id="element-14">
      <div className="max-w-6xl mx-auto" data-id="element-15">
        {/* ═══════════════════════════════════════════
            PART 1 — Village Story (existing, preserved)
            ═══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" ref={villageRef} data-id="element-16">
          {/* Text column */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={villageInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} data-id="element-17">
            <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-18">
              Our Story
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark leading-tight mb-8" data-id="element-19">
              Where Heritage
              <br data-id="element-20" />
              Meets Serenity
            </h2>
            <div className="space-y-6 text-bark-light font-body text-base md:text-lg leading-relaxed" data-id="element-21">
              <p data-id="element-22">
                In the green hills of southern Transylvania, the Saxon village
                of Richiș has been here for hundreds of years — a place where
                life moves at a slower, gentler pace.
              </p>
              <p data-id="element-23">
                We turned a traditional Saxon barn into a place of rest and
                wellness, keeping the beauty of the old building while creating
                a warm, modern space for relaxation.
              </p>
              <p data-id="element-24">
                Surrounded by old forests, vineyards, and the quiet rhythm of
                village life, Zen Barn Spa invites you to slow down, breathe
                deeply, and feel at peace.
              </p>
            </div>
          </motion.div>

          {/* Decorative visual column */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={villageInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="relative" data-id="element-25">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden" data-id="element-26">
              <img src="https://cdn.magicpatterns.com/uploads/iVPW3rS8Y8FBMndfEoa3Z8/satul-Richis-3.webp" alt="Panoramic view of the Saxon village of Richiș with traditional houses and rolling Transylvanian hills" className="w-full h-full object-cover" data-id="element-27" />
              <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(to top, rgba(61,50,41,0.25) 0%, rgba(250,247,242,0.05) 40%, rgba(212,221,208,0.08) 100%)'
            }} data-id="element-28" />
            </div>
            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-warm-white rounded-xl shadow-lg p-5 max-w-[200px]" data-id="element-29">
              <p className="font-heading text-2xl font-semibold text-terracotta mb-1" data-id="element-30">
                700+
              </p>
              <p className="font-body text-sm text-bark-light" data-id="element-31">
                Years of Saxon heritage in Richiș
              </p>
            </div>
          </motion.div>
        </div>

        {/* ═══════════════════════════════════════════
            PART 2 — Elena Buzatu
            ═══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center mt-32 md:mt-40" ref={elenaRef} data-id="element-32">
          {/* Left — Decorative portrait placeholder */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={elenaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="relative order-2 lg:order-1" data-id="element-33">
            <div className="relative aspect-[3/4] max-w-md mx-auto rounded-2xl overflow-hidden" data-id="element-34">
              {/* Elena's portrait */}
              <img src="https://cdn.magicpatterns.com/uploads/e2tvi5Xkr9aUwrB9B1WVvz/Elena-1024x1024.jpg" alt="Elena Buzatu — Spa Therapist & Trainer" className="absolute inset-0 w-full h-full object-cover" data-id="element-35" />
              {/* Warm gradient overlay to blend with site palette */}
              <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(to top, rgba(61,50,41,0.35) 0%, rgba(250,247,242,0.08) 40%, rgba(250,247,242,0) 100%)'
            }} data-id="element-36" />

              {/* Bottom label */}
              <div className="absolute bottom-6 left-0 right-0 text-center" data-id="element-37">
                <p className="font-heading text-base italic text-cream/80 drop-shadow-md" data-id="element-38">
                  Spa Therapist & Trainer
                </p>
              </div>
            </div>

            {/* Floating credential card */}
            <div className="absolute -bottom-5 -right-4 md:-right-8 bg-warm-white rounded-xl shadow-lg p-5 max-w-[220px]" data-id="element-39">
              <div className="flex items-center gap-2 mb-2" data-id="element-40">
                <LaurelIcon className="w-5 h-5 text-terracotta" data-id="element-41" />
                <p className="font-heading text-lg font-semibold text-bark" data-id="element-42">
                  20+ Years
                </p>
              </div>
              <p className="font-body text-sm text-bark-light" data-id="element-43">
                World-class spa expertise
              </p>
            </div>
          </motion.div>

          {/* Right — Elena's bio */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={elenaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="order-1 lg:order-2" data-id="element-44">
            <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-45">
              Your Therapist
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark leading-tight mb-2" data-id="element-46">
              Elena Buzatu
            </h2>
            <p className="font-heading text-xl italic text-bark-light mb-8" data-id="element-47">
              Spa Therapist & Trainer
            </p>
            <div className="space-y-6 text-bark-light font-body text-base md:text-lg leading-relaxed" data-id="element-48">
              <p data-id="element-49">
                With over 20 years of experience as a spa therapist and trainer,
                Elena has worked with some of the best wellness teams in the
                world — including the Four Seasons Hotels in Cairo and London,
                and the Mandarin Oriental in Doha.
              </p>
              <p data-id="element-50">
                Always looking for new ideas and treatments, she has travelled
                widely, learning techniques in water therapy, massage, facial
                care, and Ayurvedic healing. At Zen Barn Spa, Elena brings all
                of this knowledge and care to the heart of Transylvania.
              </p>
            </div>

            {/* Credentials */}
            <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-51">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-stone text-sm font-body font-medium text-bark-light" data-id="element-52">
                Four Seasons
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-stone text-sm font-body font-medium text-bark-light" data-id="element-53">
                Mandarin Oriental
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-stone text-sm font-body font-medium text-bark-light" data-id="element-54">
                20+ Years
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}